import type { AudioEvent } from '../game/types';

const COOLDOWN_MS: Record<AudioEvent, number> = {
  ui: 45,
  pickup: 100,
  chop: 170,
  splash: 240,
  pour: 260,
  fridge: 420,
  'timer-ready': 700,
  oven: 480,
  complete: 900,
  deliver: 700,
  fail: 1_000,
};

/** Lightweight, asset-free sound effects for the kitchen interactions. */
export class GameAudio {
  private context: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private readonly activeSources = new Set<AudioScheduledSourceNode>();
  private readonly lastPlayedAt = new Map<AudioEvent, number>();
  private noiseSeed = 0x4b414946;
  private muted = false;
  private disposed = false;

  async unlock(): Promise<void> {
    const context = this.getContext();
    if (context?.state === 'suspended') {
      await context.resume();
    }
  }

  play(event: AudioEvent): void {
    if (this.disposed || this.muted) return;

    const playedAt = Date.now();
    const previous = this.lastPlayedAt.get(event) ?? -Infinity;
    if (playedAt - previous < COOLDOWN_MS[event]) return;

    const context = this.getContext();
    if (!context || context.state === 'closed') return;

    this.lastPlayedAt.set(event, playedAt);
    if (context.state === 'suspended') {
      void context.resume().catch(() => undefined);
    }

    const now = context.currentTime + 0.008;
    switch (event) {
      case 'ui':
        this.tone(620, now, 0.06, 0.035, 'sine', 840);
        break;
      case 'pickup':
        this.tone(410, now, 0.09, 0.05, 'triangle', 560);
        this.tone(670, now + 0.045, 0.11, 0.035, 'sine', 810);
        break;
      case 'chop':
        this.noise(now, 0.075, 0.055, 'bandpass', 1_650, 1.8);
        this.tone(125, now, 0.08, 0.045, 'square', 82);
        break;
      case 'splash':
        this.noise(now, 0.3, 0.04, 'lowpass', 1_850, 0.65);
        this.tone(690, now + 0.045, 0.13, 0.018, 'sine', 430);
        this.tone(910, now + 0.12, 0.1, 0.014, 'sine', 570);
        break;
      case 'pour':
        this.noise(now, 0.38, 0.035, 'bandpass', 1_200, 0.55);
        this.tone(260, now, 0.34, 0.018, 'sine', 175);
        break;
      case 'fridge':
        this.tone(150, now, 0.22, 0.045, 'sawtooth', 92);
        this.noise(now, 0.055, 0.035, 'highpass', 2_800, 0.8);
        this.tone(78, now + 0.14, 0.12, 0.028, 'sine', 62);
        break;
      case 'timer-ready':
        this.tone(880, now, 0.14, 0.05, 'triangle', 940);
        this.tone(1_175, now + 0.17, 0.22, 0.055, 'triangle', 1_220);
        break;
      case 'oven':
        this.noise(now, 0.11, 0.045, 'bandpass', 820, 1.2);
        this.tone(118, now, 0.24, 0.05, 'triangle', 68);
        this.tone(62, now + 0.11, 0.2, 0.024, 'sine', 48);
        break;
      case 'complete':
        this.tone(523.25, now, 0.18, 0.045, 'triangle', 570);
        this.tone(659.25, now + 0.105, 0.2, 0.048, 'triangle', 710);
        this.tone(783.99, now + 0.215, 0.3, 0.055, 'sine', 850);
        break;
      case 'deliver':
        this.tone(659.25, now, 0.13, 0.04, 'sine', 700);
        this.tone(880, now + 0.085, 0.16, 0.045, 'sine', 940);
        this.tone(1_046.5, now + 0.18, 0.28, 0.05, 'triangle', 1_110);
        this.noise(now + 0.19, 0.08, 0.014, 'highpass', 3_500, 0.6);
        break;
      case 'fail':
        this.tone(330, now, 0.2, 0.045, 'sawtooth', 265);
        this.tone(220, now + 0.14, 0.25, 0.045, 'sawtooth', 178);
        this.tone(147, now + 0.3, 0.34, 0.04, 'triangle', 112);
        break;
    }
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    const context = this.context;
    const master = this.masterGain;
    if (!context || !master || context.state === 'closed') return;

    master.gain.cancelScheduledValues(context.currentTime);
    master.gain.setTargetAtTime(muted ? 0 : 0.28, context.currentTime, 0.012);
  }

  toggleMuted(): boolean {
    this.setMuted(!this.muted);
    return this.muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;

    for (const source of this.activeSources) {
      try {
        source.stop();
      } catch {
        // A source that has already ended cannot always be stopped twice.
      }
      source.disconnect();
    }
    this.activeSources.clear();
    this.lastPlayedAt.clear();
    this.masterGain?.disconnect();

    if (this.context && this.context.state !== 'closed') {
      void this.context.close().catch(() => undefined);
    }
    this.context = null;
    this.masterGain = null;
  }

  private getContext(): AudioContext | null {
    if (this.disposed || typeof AudioContext === 'undefined') return null;
    if (this.context) return this.context;

    const context = new AudioContext({ latencyHint: 'interactive' });
    const master = context.createGain();
    master.gain.value = this.muted ? 0 : 0.28;
    master.connect(context.destination);
    this.context = context;
    this.masterGain = master;
    return context;
  }

  private tone(
    frequency: number,
    start: number,
    duration: number,
    volume: number,
    type: OscillatorType,
    endFrequency: number,
  ): void {
    const context = this.context;
    const destination = this.masterGain;
    if (!context || !destination) return;

    const oscillator = context.createOscillator();
    const envelope = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(20, endFrequency), start + duration);
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(volume, start + Math.min(0.012, duration * 0.2));
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(envelope).connect(destination);

    this.track(oscillator);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.015);
  }

  private noise(
    start: number,
    duration: number,
    volume: number,
    filterType: BiquadFilterType,
    frequency: number,
    q: number,
  ): void {
    const context = this.context;
    const destination = this.masterGain;
    if (!context || !destination) return;

    const frameCount = Math.max(1, Math.ceil(context.sampleRate * duration));
    const buffer = context.createBuffer(1, frameCount, context.sampleRate);
    const samples = buffer.getChannelData(0);
    for (let index = 0; index < frameCount; index += 1) {
      const decay = 1 - index / frameCount;
      samples[index] = (this.nextNoise() * 2 - 1) * (0.45 + decay * 0.55);
    }

    const source = context.createBufferSource();
    const filter = context.createBiquadFilter();
    const envelope = context.createGain();
    source.buffer = buffer;
    filter.type = filterType;
    filter.frequency.value = frequency;
    filter.Q.value = q;
    envelope.gain.setValueAtTime(0.0001, start);
    envelope.gain.exponentialRampToValueAtTime(volume, start + Math.min(0.009, duration * 0.15));
    envelope.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    source.connect(filter).connect(envelope).connect(destination);

    this.track(source);
    source.start(start);
    source.stop(start + duration + 0.01);
  }

  private track(source: AudioScheduledSourceNode): void {
    this.activeSources.add(source);
    source.onended = () => {
      this.activeSources.delete(source);
      source.disconnect();
    };
  }

  private nextNoise(): number {
    this.noiseSeed = (Math.imul(this.noiseSeed, 1664525) + 1013904223) >>> 0;
    return this.noiseSeed / 0x100000000;
  }
}
